/** The Preferences modal's tabs: how the eleven preference sections group into a short left-hand list. Plain data, free of icons and DOM, so the React Native panel groups its rows the same way. */
import type { UserPreferences } from './preferencesSchema';
import { isPreferenceSectionId, type PreferenceSectionId } from './preferenceSectionIds';

export interface SettingsTabDefinition {
  id: string;
  label: string;
  /** Web panes, rendered top to bottom in this order. */
  sections: readonly PreferenceSectionId[];
  /** The stored preference groups this tab edits, for clients that render rows from the schema. */
  schemaKeys: readonly (keyof UserPreferences)[];
}

export const SETTINGS_TABS = [
  { id: 'general', label: 'General', sections: ['appearance', 'region', 'accessibility'], schemaKeys: ['appearance', 'region', 'accessibility'] },
  { id: 'notifications', label: 'Notifications', sections: ['notifications', 'priority-contacts'], schemaKeys: ['notifications', 'priorityContacts'] },
  { id: 'availability', label: 'Availability', sections: ['availability'], schemaKeys: ['availability'] },
  { id: 'messages', label: 'Messages', sections: ['messages'], schemaKeys: ['messages'] },
  { id: 'navigation', label: 'Navigation', sections: ['navigation'], schemaKeys: ['navigation'] },
  { id: 'privacy', label: 'Privacy', sections: ['privacy'], schemaKeys: ['privacy'] },
  { id: 'advanced', label: 'Advanced', sections: ['advanced'], schemaKeys: ['advanced'] },
] as const satisfies readonly SettingsTabDefinition[];

export type SettingsTabId = typeof SETTINGS_TABS[number]['id'];

export const DEFAULT_SETTINGS_TAB: SettingsTabId = 'general';

export function isSettingsTabId(value: string | null | undefined): value is SettingsTabId {
  return SETTINGS_TABS.some(tab => tab.id === value);
}

/** The tab that holds a tab id or a section id. Section ids are what the older /preferences/[section] addresses carry. */
export function settingsTabFor(target: string | null | undefined): SettingsTabId | null {
  if (target === 'mark-as-read') return 'messages';
  if (isSettingsTabId(target)) return target;
  if (!isPreferenceSectionId(target)) return null;
  return SETTINGS_TABS.find(tab => (tab.sections as readonly string[]).includes(target))?.id ?? null;
}

export function getSettingsTab(id: SettingsTabId): SettingsTabDefinition {
  return SETTINGS_TABS.find(tab => tab.id === id) ?? SETTINGS_TABS[0];
}
